import { create } from 'zustand'
import { YMarketOrder } from './types'
import { loadOrdersCache, saveOrdersCache } from './orders-cache'
import { salesSourceOrderKey, YANDEX_MARKET_PRIMARY_SOURCE } from './sales-sources'
import type { AuthoritativeMissionControlSummary } from './order-mission-control'

export interface BuyerInfo {
  lastName?: string
  firstName?: string
  middleName?: string
}

interface OrdersStore {
  orders: YMarketOrder[]
  buyerMap: Record<number, BuyerInfo>
  nextPageToken: string | null
  lastFetchedAt: number | null
  fetchedForStatuses: string
  /** True while the painted rows come from the browser cache, not the API. */
  ordersFromStaleCache: boolean
  missionSummary: AuthoritativeMissionControlSummary | null
  missionKey: string
  missionLoading: boolean
  missionError: string
  missionRequest: number
  ordersRequestKey: string
  ordersRequest: number

  setFetch: (orders: YMarketOrder[], token: string | null, statusKey: string) => void
  appendFetch: (
    orders: YMarketOrder[],
    token: string | null,
    expectedStatusKey: string,
  ) => { applied: boolean; newOrderCount: number }
  updateBuyerMap: (patch: Record<number, BuyerInfo>) => void
  beginOrdersFetch: (key: string) => number
  acceptOrdersFetch: (
    key: string,
    request: number,
    orders: YMarketOrder[],
    token: string | null,
    append: boolean,
  ) => { applied: boolean; newOrderCount: number }
  invalidate: () => void
  hydrateCache: (statusKey: string) => boolean
  beginMissionFetch: (key: string) => number
  acceptMissionFetch: (key: string, request: number, summary: AuthoritativeMissionControlSummary) => boolean
  failMissionFetch: (key: string, request: number, error: string) => void
}

/**
 * The merge identity of one order row.
 *
 * A bare numeric `orderId` is only unique inside one marketplace. The store
 * already carries the projected source, so the key is source-qualified and a
 * second source cannot silently overwrite a Yandex row that happens to share
 * its number. An order without a projected source is read as the primary
 * Yandex source, which is what it was before sources were qualified at all.
 */
export function orderMergeKey(order: YMarketOrder): string {
  return salesSourceOrderKey(
    order.customer?.sourceId || YANDEX_MARKET_PRIMARY_SOURCE,
    order.orderId,
  )
}

export function mergeOrdersById(
  current: readonly YMarketOrder[],
  incoming: readonly YMarketOrder[],
): YMarketOrder[] {
  const merged = new Map<string, YMarketOrder>()
  for (const order of current) merged.set(orderMergeKey(order), order)
  // A fresher page copy wins without changing the original order position.
  for (const order of incoming) merged.set(orderMergeKey(order), order)
  return [...merged.values()]
}

export const useOrdersStore = create<OrdersStore>((set) => ({
  orders: [],
  buyerMap: {},
  nextPageToken: null,
  lastFetchedAt: null,
  fetchedForStatuses: '',
  ordersFromStaleCache: false,
  missionSummary: null,
  missionKey: '',
  missionLoading: false,
  missionError: '',
  missionRequest: 0,
  ordersRequestKey: '',
  ordersRequest: 0,

  setFetch: (orders, token, statusKey) => {
    const uniqueOrders = mergeOrdersById([], orders)
    saveOrdersCache(statusKey, uniqueOrders, token)
    set({
      orders: uniqueOrders,
      nextPageToken: token,
      lastFetchedAt: Date.now(),
      fetchedForStatuses: statusKey,
      ordersFromStaleCache: false,
    })
  },

  appendFetch: (orders, token, expectedStatusKey) => {
    let result = { applied: false, newOrderCount: 0 }
    set((s) => {
      if (s.fetchedForStatuses !== expectedStatusKey) return s
      const existingIds = new Set(s.orders.map(orderMergeKey))
      const newOrderCount = orders.filter((order) => !existingIds.has(orderMergeKey(order))).length
      const merged = mergeOrdersById(s.orders, orders)
      // A page with no new identities means the provider repeated a page or
      // token. Stop automatic pagination instead of requesting it forever.
      const nextToken = newOrderCount > 0 ? token : null
      saveOrdersCache(expectedStatusKey, merged, nextToken)
      result = { applied: true, newOrderCount }
      return {
        orders: merged,
        nextPageToken: nextToken,
        lastFetchedAt: Date.now(),
        ordersFromStaleCache: false,
      }
    })
    return result
  },

  updateBuyerMap: (patch) =>
    set((s) => ({ buyerMap: { ...s.buyerMap, ...patch } })),

  beginOrdersFetch: (key) => {
    let request = 0
    set((state) => {
      request = state.ordersRequest + 1
      return { ordersRequestKey: key, ordersRequest: request }
    })
    return request
  },

  acceptOrdersFetch: (key, request, orders, token, append) => {
    let result = { applied: false, newOrderCount: 0 }
    set((state) => {
      if (state.ordersRequestKey !== key || state.ordersRequest !== request) return state
      if (append) {
        if (state.fetchedForStatuses !== key) return state
        const existingIds = new Set(state.orders.map(orderMergeKey))
        const newOrderCount = orders.filter((order) => !existingIds.has(orderMergeKey(order))).length
        const merged = mergeOrdersById(state.orders, orders)
        const nextToken = newOrderCount > 0 ? token : null
        saveOrdersCache(key, merged, nextToken)
        result = { applied: true, newOrderCount }
        return {
          orders: merged,
          nextPageToken: nextToken,
          lastFetchedAt: Date.now(),
          ordersFromStaleCache: false,
        }
      }
      const uniqueOrders = mergeOrdersById([], orders)
      saveOrdersCache(key, uniqueOrders, token)
      result = { applied: true, newOrderCount: uniqueOrders.length }
      return {
        orders: uniqueOrders,
        nextPageToken: token,
        lastFetchedAt: Date.now(),
        fetchedForStatuses: key,
        ordersFromStaleCache: false,
      }
    })
    return result
  },

  invalidate: () => set({ lastFetchedAt: null }),

  hydrateCache: (requestedStatusKey) => {
    const cached = loadOrdersCache(requestedStatusKey)
    if (!cached) return false
    const uniqueOrders = mergeOrdersById([], cached.orders)
    // A cached render is marked as such. An unmarked one is indistinguishable
    // from a live answer, which is how a six-hour-old status could be read as
    // the current one.
    set({
      orders: uniqueOrders,
      nextPageToken: cached.nextPageToken,
      lastFetchedAt: cached.savedAt,
      fetchedForStatuses: requestedStatusKey,
      ordersFromStaleCache: cached.stale,
    })
    return true
  },

  beginMissionFetch: (key) => {
    let request = 0
    set((state) => {
      request = state.missionRequest + 1
      return {
        missionKey: key,
        missionSummary: state.missionKey === key ? state.missionSummary : null,
        missionLoading: true,
        missionError: '',
        missionRequest: request,
      }
    })
    return request
  },

  acceptMissionFetch: (key, request, summary) => {
    let accepted = false
    set((state) => {
      if (state.missionKey !== key || state.missionRequest !== request || !summary.complete) return state
      const currentTime = state.missionSummary ? Date.parse(state.missionSummary.generatedAt) : Number.NEGATIVE_INFINITY
      const incomingTime = Date.parse(summary.generatedAt)
      // A forced refresh can observe the same backend generation after a
      // client-side stale render. Its complete payload must still replace it;
      // only an older generation is rejected.
      if (!Number.isFinite(incomingTime) || incomingTime < currentTime) {
        return { missionLoading: false }
      }
      accepted = true
      return { missionSummary: summary, missionLoading: false, missionError: '' }
    })
    return accepted
  },

  failMissionFetch: (key, request, error) => set((state) => (
    state.missionKey === key && state.missionRequest === request
      ? { missionLoading: false, missionError: error }
      : state
  )),
}))

export function statusKey(statuses: Set<string>): string {
  return [...statuses].sort().join(',')
}
